'use client'

import * as React from 'react'
import { Search, X, Loader2, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSearchStore } from '@/lib/store'
import type { RecentSearch, TransactionType } from '@/lib/types'
import { MobileSearchSheet } from './mobile-search-sheet'
import { RecentSearchesDropdown } from './recent-searches-dropdown'
import { useDictation, DictationMicButton } from './dictation'
import { useI18n } from '@/lib/i18n'

interface CompactSearchInputProps {
  onSearch: () => void
  className?: string
}

export function CompactSearchInput({ onSearch, className }: CompactSearchInputProps) {
  const { t } = useI18n()
  const { query, setQuery, isLoading, filters, updateFilter, selectedCity, selectedDistricts, setFilters, clearFilters, clearTags, addTag, setSelectedCity, setSelectedDistricts, setTravelTime, setSort } = useSearchStore()
  // Dictation appends into the committed query (the desktop input binds the
  // store directly, unlike the sheet's local draft).
  const dictation = useDictation(() => query, setQuery)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false)
  const [showRecentSearches, setShowRecentSearches] = React.useState(false)
  const [transactionTypeDropdownOpen, setTransactionTypeDropdownOpen] = React.useState(false)
  const [inputLeftPadding, setInputLeftPadding] = React.useState(140)
  const recentSearchesRef = React.useRef<HTMLDivElement>(null)
  const transactionTypeDropdownRef = React.useRef<HTMLDivElement>(null)
  const transactionTypeButtonRef = React.useRef<HTMLButtonElement>(null)

  // Calculate input left padding based on transaction type button width.
  // ResizeObserver keeps it correct across webfont swap, breakpoint
  // reveal (the form is hidden below lg, measuring 0), and language switch.
  React.useEffect(() => {
    const btn = transactionTypeButtonRef.current
    if (!btn) return
    const measure = () => {
      if (btn.offsetWidth > 0) {
        setInputLeftPadding(btn.offsetWidth + 20)
      }
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(btn)
    return () => ro.disconnect()
  }, [filters.transaction_type])

  // Close dropdowns on click outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (recentSearchesRef.current && !recentSearchesRef.current.contains(event.target as Node)) {
        setShowRecentSearches(false)
      }
      if (transactionTypeDropdownRef.current && !transactionTypeDropdownRef.current.contains(event.target as Node)) {
        setTransactionTypeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectRecentSearch = React.useCallback((search: RecentSearch) => {
    setShowRecentSearches(false)
    if (search.type === 'text' && search.query) {
      setQuery(search.query)
      clearFilters()
    } else {
      setQuery('')
      if (search.filters) setFilters({ ...search.filters })
      clearTags()
      search.tags?.forEach((tag) => addTag(tag))
    }
    if (search.city) setSelectedCity(search.city)
    if (search.districts) setSelectedDistricts(search.districts)
    if (search.travelTime !== undefined) setTravelTime(search.travelTime)
    if (search.sort) setSort(search.sort)
    // Dispatch with the correct source so search page uses 'text' or 'filters' accordingly
    const source = search.type === 'filters' ? 'filters' : 'text'
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('header-search-triggered', { detail: { source } }))
    }, 0)
  }, [setQuery, clearFilters, setFilters, clearTags, addTag, setSelectedCity, setSelectedDistricts, setTravelTime, setSort])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setShowRecentSearches(false)
    onSearch()
  }

  const handleClear = () => {
    setQuery('')
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (showRecentSearches) {
        setShowRecentSearches(false)
      } else {
        handleClear()
      }
    }
  }

  // Build display text for mobile pill
  const mobileDisplayText = React.useMemo(() => {
    if (query) return query
    return t('search.searchApartmentsPlaceholder', 'Search apartments...')
  }, [query, t])

  // Build location subtitle for mobile
  const mobileSubtitle = React.useMemo(() => {
    const parts: string[] = []
    if (selectedCity) parts.push(selectedCity)
    if (selectedDistricts.length > 0) {
      parts.push(selectedDistricts.length === 1 ? selectedDistricts[0] : `${selectedDistricts.length} ${t('search.areas', 'areas')}`)
    }
    return parts.length > 0 ? parts.join(' · ') : undefined
  }, [selectedCity, selectedDistricts, t])

  return (
    <>
      {/* Mobile: tappable trigger that opens the search sheet. Below sm the
          header is too crowded for text — the pill squeezed to ~60px and the
          query/subtitle truncated to two letters ("H.. / P..") — so it
          collapses to a plain icon button sized like the neighbouring header
          icons (the query stays visible in the results headline and the
          merged filter row). From sm up there's room for the labelled pill. */}
      <button
        type="button"
        onClick={() => setMobileSearchOpen(true)}
        aria-label={t('search.searchButton', 'Search')}
        className={cn(
          'lg:hidden flex items-center rounded-full',
          'transition-all duration-200',
          // <sm: a 48px PRIMARY-tinted circle. It was a 40px card/50 circle
          // with a muted-grey glyph — under the 44px touch minimum, and
          // styled fainter than the heart/bell/globe beside it even though
          // searching is the whole point of the app, so it read as just
          // another header icon. The primary tint is the same one the
          // Filters pill and the rent/sale dropdown already use.
          'h-12 w-12 flex-none justify-center',
          'border border-primary/25 bg-primary/10 active:bg-primary/20',
          // ≥sm: the labelled pill, visually unchanged.
          'sm:h-16 sm:w-auto sm:min-w-0 sm:flex-1 sm:justify-start sm:gap-3 sm:px-6',
          'sm:border-border/40 sm:bg-card/50 sm:hover:border-border sm:active:bg-muted/60',
          className
        )}
      >
        {isLoading ? (
          <Loader2 className="h-[22px] w-[22px] text-primary animate-spin flex-shrink-0 sm:h-6 sm:w-6" />
        ) : (
          <Search className="h-[22px] w-[22px] text-primary flex-shrink-0 sm:h-6 sm:w-6 sm:text-muted-foreground" />
        )}
        <div className="hidden sm:block flex-1 min-w-0 text-left">
          <p className="text-base font-medium truncate text-foreground">
            {mobileDisplayText}
          </p>
          {mobileSubtitle && (
            <p className="text-sm text-muted-foreground truncate">
              {mobileSubtitle}
            </p>
          )}
        </div>
      </button>

      {/* Desktop: Regular search input */}
      <form onSubmit={handleSubmit} className={cn('hidden lg:flex relative items-center', className)}>
        <div ref={recentSearchesRef} className="relative flex-1">
          {/* Transaction type dropdown */}
          <div ref={transactionTypeDropdownRef} className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
            <button
              ref={transactionTypeButtonRef}
              type="button"
              onClick={() => setTransactionTypeDropdownOpen(!transactionTypeDropdownOpen)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full',
                'bg-primary/10 text-primary text-sm font-medium',
                'hover:bg-primary/20 transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-primary/30'
              )}
            >
              <span>{t(`search.${filters.transaction_type || 'rent'}`, filters.transaction_type || 'rent')}</span>
              <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', transactionTypeDropdownOpen && 'rotate-180')} />
            </button>
            {transactionTypeDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 min-w-[120px] rounded-lg bg-popover border border-border shadow-lg">
                {(['rent', 'sale'] as TransactionType[]).map((transactionType) => (
                  <button
                    key={transactionType}
                    type="button"
                    onClick={() => { updateFilter('transaction_type', transactionType); setTransactionTypeDropdownOpen(false) }}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm',
                      'hover:bg-accent transition-colors',
                      (filters.transaction_type || 'rent') === transactionType ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                    )}
                  >
                    {t(`search.${transactionType}`, transactionType)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowRecentSearches(true) }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowRecentSearches(true)}
            placeholder={t('search.examplePlaceholder', '2 rooms, Praha 7, below 20k...')}
            className={cn(
              'w-full h-16 rounded-full',
              // Right padding clears [× clear][mic][Search] — mic only on
              // voice-capable browsers.
              dictation.supported ? 'pr-[184px]' : 'pr-36',
              'bg-card/50 border border-border/40',
              'text-base text-foreground placeholder:text-muted-foreground',
              'focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20',
              'transition-all duration-200'
            )}
            style={{ paddingLeft: `${inputLeftPadding}px` }}
            disabled={isLoading}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {query && !isLoading && (
              <button
                type="button"
                onClick={handleClear}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            )}
            {dictation.supported && !isLoading && (
              <DictationMicButton listening={dictation.listening} onToggle={dictation.toggle} className="p-2" />
            )}
            {isLoading ? (
              <Loader2 className="h-6 w-6 text-primary animate-spin mr-2" />
            ) : (
              <button
                type="submit"
                disabled={isLoading}
                className={cn(
                  'h-11 px-6 rounded-full text-sm font-medium',
                  'bg-primary text-white',
                  'hover:bg-primary/90 active:bg-primary/80',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-all duration-200',
                  'flex items-center justify-center'
                )}
              >
                {t('search.searchButton', 'Search')}
              </button>
            )}
          </div>

          {/* Recent searches dropdown */}
          <RecentSearchesDropdown
            visible={showRecentSearches}
            filterText={query}
            onSelect={handleSelectRecentSearch}
          />
        </div>
      </form>

      {/* Mobile search modal */}
      <MobileSearchSheet
        open={mobileSearchOpen}
        onOpenChange={setMobileSearchOpen}
        onSearch={onSearch}
      />
    </>
  )
}

